/**
 * KAN-757, KAN-758: выбор периода доски CRM одним полем. Кнопка с подписью
 * периода ("9 октября", "1 - 9 октября"), по клику всплывающее окно:
 * быстрые периоды и календарь. Первый клик по дню - начало, второй - конец
 * (тот же день дважды = один день); кнопка "Применить" берёт начатый день как
 * период из одного дня. Даты по Москве, период не длиннее 366 дней.
 * KAN-883: одно поле периода и для таблицы CRM, "Лиды по дням" и "Лиды по
 * теплоте": onAllTime добавляет быстрый пункт "Всё время" (range null - подпись
 * "Всё время"); testId - data-testid корня (по умолчанию crm-range).
 * KAN-906: compact - кнопка высотой h-8.
 * KAN-910: onPreset - быстрый пункт окна уходит именем (Сегодня, Вчера, 7 дней, 30 дней);
 * даты - только при выборе дней в календаре.
 * KAN-963: быстрые периоды только здесь (не рядом в панели): сверху окна ряд пунктов из
 * choices (текущий aria-pressed, data-testid="crm-range-presets"), ниже календарь.
 * Подпись кнопки - periodButtonLabel ("7 дней", "Всё время", "29 октября").
 * Esc возвращает фокус на кнопку; кнопка в правой половине экрана - окно по её правому краю.
 */
import { useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CrmRangeCalendar } from '@/components/crm/CrmRangeCalendar';
import { clampCrmRange, monthOf, type MonthRef } from '@/lib/crmRange';
import { todayMoscow, type CrmRange } from '@/lib/crmTable';
import {
  PERIOD_PRESETS,
  activePeriodChoice,
  periodButtonLabel,
  periodChoiceLabel,
  periodPresetRange,
  type PeriodChoice,
  type PeriodPreset,
} from '@/lib/periodPresets';

interface CrmRangePickerProps {
  /** null - "Всё время" (только вместе с onAllTime). */
  range: CrmRange | null;
  onChange: (range: CrmRange) => void;
  /** Быстрый пункт "Всё время". */
  onAllTime?: () => void;
  /** KAN-910: быстрый пункт именем; без него - датами в onChange. */
  onPreset?: (preset: PeriodPreset) => void;
  testId?: string;
  /** KAN-906: компактная кнопка (h-8). */
  compact?: boolean;
  /** KAN-963: быстрые пункты окна; по умолчанию все, "Всё время" при onAllTime. */
  choices?: readonly PeriodChoice[];
}

const DEFAULT_CHOICES: readonly PeriodChoice[] = [...PERIOD_PRESETS, 'all'];

export function CrmRangePicker({
  range,
  onChange,
  onAllTime,
  onPreset,
  testId = 'crm-range',
  compact = false,
  choices = DEFAULT_CHOICES,
}: CrmRangePickerProps) {
  const today = todayMoscow();
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState<string | null>(null);
  const [month, setMonth] = useState<MonthRef>(() => monthOf(range?.to ?? today));
  const label = periodButtonLabel(range, today);
  // "Всё время" только когда его можно выбрать.
  const shown = choices.filter((choice) => choice !== 'all' || !!onAllTime);
  const active = activePeriodChoice(range, shown, today);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Кнопка в правой половине экрана: окно выровнено по её правому краю, чтобы не вылезать за экран.
  const [alignRight, setAlignRight] = useState(false);

  const close = () => {
    setOpen(false);
    setDraftStart(null);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      // Фокус обратно на кнопку: клавиатура не теряет место.
      triggerRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    setMonth(monthOf(range?.to ?? today));
    const rect = rootRef.current?.getBoundingClientRect();
    setAlignRight(!!rect && rect.left + rect.width / 2 > window.innerWidth / 2);
    setOpen(true);
  };

  const apply = (next: CrmRange) => {
    onChange(clampCrmRange(next));
    close();
  };

  const pick = (day: string) => {
    if (!draftStart) {
      setDraftStart(day);
      return;
    }
    apply(day < draftStart ? { from: day, to: draftStart } : { from: draftStart, to: day });
  };

  const choose = (choice: PeriodChoice) => {
    if (choice === 'all') {
      onAllTime?.();
      close();
    } else if (onPreset) {
      onPreset(choice);
      close();
    } else {
      apply(periodPresetRange(choice, today));
    }
  };

  return (
    <div
      ref={rootRef}
      className="relative inline-block text-sm"
      data-testid={testId}
      // Выбранный быстрый пункт (пусто - свой период): видно и при закрытом окне.
      data-active-choice={active ?? ''}
    >
      <Button
        ref={triggerRef}
        type="button"
        size="sm"
        variant="outline"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Период: ${label}`}
        className={compact ? 'h-8 px-2.5' : undefined}
        onClick={toggle}
      >
        <CalendarDays className="mr-1.5 h-4 w-4 text-gray-500" />
        <span data-testid="crm-range-label" className="whitespace-nowrap">
          {label}
        </span>
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label="Выбор периода"
          data-align={alignRight ? 'right' : 'left'}
          className={`absolute ${alignRight ? 'right-0' : 'left-0'} z-30 mt-1 flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg`}
        >
          {shown.length > 0 && (
            <div data-testid="crm-range-presets" className="flex max-w-[17rem] flex-wrap gap-1">
              {shown.map((choice) => {
                const on = choice === active;
                return (
                  <Button
                    key={choice}
                    type="button"
                    size="sm"
                    variant={on ? 'default' : 'outline'}
                    aria-pressed={on}
                    className="h-8 px-2.5"
                    onClick={() => choose(choice)}
                  >
                    {periodChoiceLabel(choice)}
                  </Button>
                );
              })}
            </div>
          )}
          <div className="flex flex-col gap-2">
            <CrmRangeCalendar
              month={month}
              onMonthChange={setMonth}
              range={range}
              draftStart={draftStart}
              today={today}
              onPick={pick}
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-gray-500">
                {draftStart ? 'Выберите конец периода' : 'Выберите начало периода'}
              </span>
              <Button
                type="button"
                size="sm"
                disabled={!draftStart}
                onClick={() => draftStart && apply({ from: draftStart, to: draftStart })}
              >
                Применить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
